'use client';

import React from 'react';
import { Step1Form } from './step1/Step1Form';
import { Step2Form } from './step2/Step2Form';
import { Step3Form } from './step3/Step3Form';
import { Step1Aside } from './step1/Step1Aside';
import { Step2Aside } from './step2/Step2Aside';
import { Step3Aside } from './step3/Step3Aside';
import { StepSuccessfulAside } from './success/StepSuccessfulAside';
import { Successful } from './success/Successful';
import { useRegistrationForm } from './hooks/useRegistrationForm';
import { useRegistrationSubmission } from './hooks/useRegistrationSubmission';
import { validateStep1, validateStep2, validateStep3 } from '@/features/b2b/register/services/registrationService';
import { scrollToFirstError } from '@/lib/utils';
import { ADVISER_PORTAL } from '@/constant/external-links';

const components = {
  Step1Aside,
  Step1Form,
  Step2Aside,
  Step2Form,
  Step3Aside,
  Step3Form,
  StepSuccessfulAside,
};

export const Register = () => {
  const {
    formData,
    errors,
    setErrors,
    handleInputChange,
    handleSelectChange,
    nextStep,
    prevStep,
    getRequestData,
  } = useRegistrationForm();

  const {
    isSubmitting,
    isSuccessful,
    isCaptchaValid,
    errorMessage,
    setIsCaptchaValid,
    setTurnstileToken,
    setErrorMessage,
    submitRegistrationNotificationAbandoned,
    submitRegistration,
  } = useRegistrationSubmission();

  const requestData = getRequestData();

  const handleSubmitClick = () => {
    // Validate the current step with our custom validation
    let stepErrors = {};
    if (formData.step === 1) {
      stepErrors = validateStep1(formData[1]);
    } else if (formData.step === 2) {
      stepErrors = validateStep2(formData[2]);
    } else if (formData.step === 3) {
      stepErrors = validateStep3(formData[3]);
    }

    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      scrollToFirstError();
      return;
    }

    if (formData.step === 1) {
      submitRegistrationNotificationAbandoned(requestData);
    }

    if (formData.step === 3) {
      submitRegistration(requestData);
      return;
    }

    nextStep();
  };

  const handleBackClick = () => {
    prevStep();
  };

  const CurrentStepForm = components[`Step${formData.step}Form`];
  const CurrentStepAside = components[`Step${formData.step}Aside`];

  return (
    <>
      <div className="bg-[#101733] flex items-center justify-between p-4 lg:hidden">
        <div className="flex items-center space-x-2">
          <a href="/professionals">
            <img src="/img/pure-reg.svg" alt="Logo" />
          </a>
        </div>
        <button
          onClick={handleBackClick}
          className="text-white hover:text-gray-300"
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="29" viewBox="0 0 28 29" fill="none" aria-hidden="true">
            <path d="M22.1666 14.3158H5.83331M5.83331 14.3158L14 22.4824M5.83331 14.3158L14 6.14911" stroke="#F3F4F8" strokeWidth="2.33333" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      {isSuccessful ? (
        <Successful />
      ): (
        <>
          <div className="mx-auto lg:mx-0 lg:flex lg:h-screen lg:overflow-hidden">
            <div className="hidden lg:block lg:w-5/12 2xl:w-5/12 h-screen">
              <div className={`h-full bg-[#006EB7]${formData.step > 1 ? ' relative overflow-hidden' : ''}`}>
                <CurrentStepAside step={formData.step} handleBackClick={handleBackClick} />
                {formData.step > 1 && (
                  <p className="text-white text-center font-450 text-base -mt-4">
                    Having trouble? <a href="/professionals/contact-us" className="underline font-semibold">Contact us here</a>
                  </p>
                )}
              </div>
            </div>
            <div id="register-scroll-container" className="lg:w-7/12 lg:h-screen lg:overflow-y-auto">
              <div className="py-10 container mx-auto px-6 md:px-20 xl:px-36 lg:px-24">
                <form id="form">
                  <CurrentStepForm
                    formData={formData[formData.step]}
                    handleInputChange={handleInputChange}
                    handleSelectChange={handleSelectChange}
                    handleSubmitClick={handleSubmitClick}
                    isSubmitting={isSubmitting}
                    errors={errors}
                    setErrors={setErrors}
                    setIsCaptchaValid={setIsCaptchaValid}
                    setTurnstileToken={setTurnstileToken}
                    setErrorMessage={setErrorMessage}
                    errorMessage={errorMessage}
                  />
                </form>

                <p className="text-base text-[#373945] font-450 text-center">
                  Already have an account? <a href={ADVISER_PORTAL.LOGIN} rel="nofollow" className="plain-text-link plain-text-link--underline">Log in</a>
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
